/** Money conversion for the editor.
 *
 * The backend stores an integer count of minor units and that is what the API
 * sends and accepts. The obvious `Math.round(parseFloat(text) * 100)` is wrong
 * often enough to matter — 4.20 * 100 is 420.00000000000006 in IEEE 754, and
 * 1.005 rounds the wrong way — so the text is split on the decimal point and
 * the two halves are combined as integers. No float ever touches a price.
 */

/** "425.5" -> 42550. Returns null when the text is not a usable amount. */
export function parseMoneyToMinor(text: string): number | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const match = /^(-?)(\d*)(?:[.,](\d{0,2}))?$/.exec(trimmed);
  if (!match) return null;

  const [, sign, wholeRaw, fractionRaw] = match;
  const whole = wholeRaw ?? "";
  if (!whole && !fractionRaw) return null;

  const major = whole ? Number.parseInt(whole, 10) : 0;
  if (!Number.isSafeInteger(major)) return null;

  const minor = Number.parseInt((fractionRaw ?? "").padEnd(2, "0") || "0", 10);
  const total = major * 100 + minor;
  return sign === "-" ? -total : total;
}

/** 42550 -> "425.50". The editing form's inverse of parseMoneyToMinor. */
export function formatMinorForInput(minorUnits: number | null): string {
  if (minorUnits === null) return "";
  const negative = minorUnits < 0;
  const abs = Math.abs(minorUnits);
  const major = Math.trunc(abs / 100);
  const minor = abs % 100;
  return `${negative ? "-" : ""}${major}.${String(minor).padStart(2, "0")}`;
}

/**
 * Whether these two prices are a real discount.
 *
 * The order path's definition, and the only one this dashboard uses: money is
 * forgone when a sale price is SET and is BELOW the base price. That is exactly
 * when `order_service` records `original_unit_price_minor` and adds to
 * `savings_minor`.
 *
 * A sale price EQUAL to the base is storable — the schema allows it — and is
 * not a discount: the customer pays the same number, the order records no
 * saving, and the phone shows no badge. Treating "a sale price exists" as the
 * test would put such a product on a discount screen that cannot explain what
 * it is discounting.
 */
export function isDiscounted(
  baseMinor: number | null,
  saleMinor: number | null,
): boolean {
  if (baseMinor === null || saleMinor === null) return false;
  return baseMinor > 0 && saleMinor < baseMinor;
}

/**
 * The saving as a whole percent, for DISPLAY ONLY.
 *
 * THE ONE ADMIN IMPLEMENTATION. There were two: this arithmetic, and a
 * `Math.round(((base - sale) / base) * 100)` in the product intake. They
 * disagreed — 3000 and 1900 read as 36% to a shopper and 37% to an
 * administrator — because the phone floors integer minor units
 * (`salePercentFrom` in lib/services/catalog_mapper.dart) and the intake
 * rounded a float. The number an administrator manages a discount by has to be
 * the number the shopper is shown, so this is the phone's arithmetic
 * transcribed, and the intake now reaches for it rather than keeping its own.
 *
 * Integer only: money never touches a float, and neither does a ratio of it.
 *
 * Returns null when the pair cannot express a saving — an absent sale, a base
 * of nothing, or a sale at or above the base. Null is "no percentage to show",
 * NOT "zero percent": a discount of less than one percent floors to 0 here
 * while still being a real reduction, so a caller renders that as such rather
 * than as "0% off".
 *
 * Never persisted, and never sent in a request body. A stored percentage would
 * be a second discount model free to disagree with the prices it describes.
 */
export function discountPercent(
  baseMinor: number | null,
  saleMinor: number | null,
): number | null {
  if (!isDiscounted(baseMinor, saleMinor)) return null;
  const base = baseMinor as number;
  const sale = saleMinor as number;

  // Both operands are integer minor units, so the product is an exact integer
  // and the one division below is the floor division Dart's `~/` performs. The
  // guard is what makes that claim checkable rather than assumed: within the
  // safe-integer range a quotient that is not itself an integer is at least
  // 1/base away from one, which is many orders of magnitude larger than the
  // rounding error of a double at these magnitudes — so the floor cannot be
  // nudged up by one. Outside it, nothing is claimed and nothing is shown.
  const numerator = (base - sale) * 100;
  if (!Number.isSafeInteger(numerator)) return null;
  return Math.floor(numerator / base);
}

/**
 * The saving as an administrator reads it, or null when there is no discount.
 *
 * "<1%" rather than "0%" for a reduction too small to floor to a whole percent.
 * The two are not the same statement: money really is being forgone, and a row
 * labelled "0% off" beside a lowered price says the opposite of what is true.
 * The predicate remains the amount — `isDiscounted` — and never the percentage.
 */
export function discountLabel(
  baseMinor: number | null,
  saleMinor: number | null,
): string | null {
  const percent = discountPercent(baseMinor, saleMinor);
  if (percent === null) return null;
  return percent === 0 ? "<1%" : `${percent}%`;
}
